import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs/operators';
import { NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';

import { AppTone } from '../../../core/models/ui-tone.model';
import { STORAGE_KEYS } from '../../../core/config/ui.constants';
import { AuthService } from '../../../core/services/auth.service';
import { NotificationCenterService } from '../../../core/services/notification-center.service';
import { formatDateTimePanama } from '../../../core/utils/date.utils';
import { DialogComponent } from '../../../shared/components/dialog/dialog.component';
import { DrawerComponent } from '../../../shared/components/drawer/drawer.component';
import { EmptyStateComponent } from '../../../shared/components/empty-state/empty-state.component';
import { LoadingStateComponent } from '../../../shared/components/loading-state/loading-state.component';

/** Tope del contador de la campanita: más que esto se muestra como "9+". */
const CONTADOR_MAXIMO = 9;

/** Un aviso ya listo para pintar: la plantilla solo lee campos. */
interface AvisoView {
    id: string;
    title: string;
    detail: string;
    fecha: string;
    icon: string;
    tone: AppTone;
    read: boolean;
}

/**
 * Campanita del encabezado: la bandeja de notificaciones del usuario.
 *
 * No sabe de aprobaciones ni de ningún módulo: pinta lo que publica
 * `NotificationCenterService` y navega a donde diga cada aviso.
 */
@Component({
    selector: 'app-notifications-bell',
    standalone: true,
    imports: [NgTemplateOutlet, TooltipModule, DialogComponent, DrawerComponent, EmptyStateComponent, LoadingStateComponent],
    templateUrl: './notifications-bell.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsBellComponent {
    private readonly center = inject(NotificationCenterService);
    private readonly router = inject(Router);
    private readonly auth = inject(AuthService);

    /** El panel lateral de la campanita: se abre cuando el usuario lo pide. */
    readonly panelAbierto = signal(false);

    /**
     * El saludo al entrar, que va al frente con el fondo atenuado.
     *
     * Es otro estado y no el mismo panel a propósito: a la campanita se entra queriendo, y un
     * panel lateral no estorba; el saludo no lo pidió nadie y tiene algo esperando decisión,
     * así que se pone delante.
     */
    readonly saludoAbierto = signal(false);

    readonly noLeidas = this.center.unreadCount;
    readonly hayNoLeidas = this.center.hayNoLeidas;
    readonly loading = this.center.loading;
    readonly failed = this.center.failed;

    /** El contador no crece sin límite: con muchos avisos basta con saber que hay varios. */
    readonly contador = computed(() => {
        const total = this.noLeidas();
        return total > CONTADOR_MAXIMO ? `${CONTADOR_MAXIMO}+` : String(total);
    });

    readonly tooltip = computed(() => {
        if (this.failed()) {
            return 'No se pudieron consultar las notificaciones';
        }
        const total = this.noLeidas();
        if (total === 0) {
            return 'No tienes notificaciones sin leer';
        }
        return total === 1 ? '1 notificación sin leer' : `${total} notificaciones sin leer`;
    });

    readonly subtitulo = computed(() => {
        const total = this.noLeidas();
        if (total === 0) {
            return 'Estás al día.';
        }
        return total === 1 ? '1 sin leer.' : `${total} sin leer.`;
    });

    readonly avisos = computed<AvisoView[]>(() =>
        this.center.notifications().map((aviso) => ({
            id: aviso.id,
            title: aviso.title,
            detail: aviso.detail,
            fecha: formatDateTimePanama(aviso.occurredAt),
            icon: aviso.icon,
            tone: aviso.tone,
            read: aviso.read,
        })),
    );

    readonly mostrarVacio = computed(() => !this.loading() && this.avisos().length === 0);

    constructor() {
        this.saludarAlEntrar();
    }

    /**
     * Al entrar, si hay avisos sin leer, se abre la bandeja sola.
     *
     * Espera a que el centro haya consultado de verdad (`listo`): antes de eso un cero solo
     * significa que todavía no se sabe. Y ocurre **una vez por entrada**: se mira la bandeja
     * en ese momento y se suelta, así que ni recargar la vuelve a abrir ni un aviso que
     * llegue más tarde interrumpe a quien está trabajando —para eso está el contador—.
     */
    private saludarAlEntrar(): void {
        toObservable(this.center.listo)
            .pipe(
                filter(Boolean),
                take(1),
                takeUntilDestroyed(),
            )
            .subscribe(() => {
                if (this.yaSaludado()) {
                    return;
                }
                this.marcarSaludado();
                // Se lee aquí, fuera de todo seguimiento: el saludo mira los avisos una vez,
                // no se queda pendiente de ellos.
                if (this.center.hayNoLeidas()) {
                    this.saludoAbierto.set(true);
                }
            });
    }

    /** Ya se saludó en esta pestaña con este usuario. */
    private yaSaludado(): boolean {
        const clave = this.claveSaludo();
        try {
            return Boolean(clave) && sessionStorage.getItem(clave) === '1';
        } catch {
            // Sin almacenamiento disponible se saluda igual: es preferible a no avisar.
            return false;
        }
    }

    private marcarSaludado(): void {
        const clave = this.claveSaludo();
        if (!clave) {
            return;
        }
        try {
            sessionStorage.setItem(clave, '1');
        } catch {
            // Que no se pueda recordar no es motivo para no saludar.
        }
    }

    /** La marca es por usuario: cambiar de cuenta en la misma pestaña vuelve a saludar. */
    private claveSaludo(): string {
        const usuario = String(this.auth.currentUser()?.email ?? '').trim().toLowerCase();
        return usuario ? `${STORAGE_KEYS.notificationsGreeted}:${usuario}` : '';
    }

    abrirPanel(): void {
        this.panelAbierto.set(true);
        // Al abrir se consulta de nuevo: el sondeo es de un minuto y aquí sí urge estar al día.
        this.center.refresh();
    }

    cerrarPanel(): void {
        this.panelAbierto.set(false);
    }

    /** Cierra el saludo; los avisos siguen sin leer y la campanita los sigue contando. */
    cerrarSaludo(): void {
        this.saludoAbierto.set(false);
    }

    abrirAviso(id: string): void {
        const aviso = this.center.notifications().find((candidato) => candidato.id === id);
        if (!aviso) {
            return;
        }
        this.center.markRead(aviso.id);
        this.cerrarPanel();
        this.cerrarSaludo();
        // PENDIENTE: esta navegación no llega a ocurrir cuando sale de un clic hecho dentro
        // del overlay —pasa igual desde el panel lateral de la campanita, que ya era así—.
        // El router se queda sin resolver, y con él las navegaciones siguientes. Llamar a
        // este método fuera de un clic sí navega, y retrasarla no lo arregla: apunta a la
        // transición de vista del router (`withViewTransitions`) contra el cierre del overlay.
        void this.router.navigate([...aviso.routerLink], { queryParams: aviso.queryParams });
    }

    marcarTodas(): void {
        this.center.markAllRead();
    }
}
